import type { DeepString } from "@/lib/i18n";

/**
 * CCategoryAdvertisement - polskie tlumaczenia (kategoria ogloszen - lista + artykul).
 *
 * `resultsTitle(n)` - "Znaleziono 7 ogloszen" (PL plural rules).
 * `formatPrice(p)` - "199,00 zl" (Intl.NumberFormat).
 */
export const pl = {
  emptyData: "Brak danych do wyświetlenia.",
  articleNotFound: "Ogłoszenie nie istnieje lub zostało usunięte.",
  noAdsInCategory: "Brak ogłoszeń w tej kategorii.",
  noAdsFound: "Nie znaleziono ogłoszeń pasujących do podanych kryteriów.",

  /* Search */
  searchTitle: "Wyszukiwarka",
  searchBtn: "Szukaj",
  allTypes: "Wszystkie typy",
  fields: {
    keyword: "Fraza",
    type: "Typ",
    priceFrom: "Cena od",
    priceTo: "Cena do",
  },
  validation: {
    invalidPrice: "Niepoprawna wartość ceny (max 2 miejsca po przecinku).",
  },

  /* Search results title */
  resultsTitle: (n: number): string => {
    const abs = Math.abs(n);
    const mod10 = abs % 10;
    const mod100 = abs % 100;
    if (abs === 1) return "Znaleziono 1 ogłoszenie";
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14))
      return `Znaleziono ${n} ogłoszenia`;
    return `Znaleziono ${n} ogłoszeń`;
  },

  /* List card */
  price: "Cena",
  negotiatedPrice: "Cena do uzgodnienia",
  priceNotSet: "-",
  readMore: "Czytaj więcej ->",
  formatPrice: (value: number): string => {
    try {
      return new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: "PLN",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `${value.toFixed(2)} zł`;
    }
  },

  /* Article view */
  backToList: "<- Wróć do listy",
  addAdvert: "Dodaj ogłoszenie",

  /* Contact box */
  contact: "Kontakt",
  city: "Miejscowość",
  telephone: "Telefon",
  email: "E-mail",

  /* Contact form (do wystawcy ogloszenia) */
  contactForm: {
    title: "Wyślij wiadomość do wystawcy",
    submit: "Wyślij",
    sending: "Wysyłanie...",
    fields: {
      name: "Imię i nazwisko",
      email: "E-mail",
      telephone: "Telefon (opcjonalnie)",
      text: "Treść wiadomości",
      personalData:
        "Wyrażam zgodę na przetwarzanie moich danych osobowych w celu odpowiedzi na wiadomość.",
    },
    validation: {
      requiredName: "Podaj imię i nazwisko.",
      requiredEmail: "Podaj adres e-mail.",
      invalidEmail: "Niepoprawny adres e-mail.",
      requiredText: "Wpisz treść wiadomości.",
      requiredPersonalData:
        "Wymagana zgoda na przetwarzanie danych osobowych.",
    },
    status: {
      sendSuccess: "Wiadomość została wysłana. Dziękujemy!",
      sendFailed:
        "Nie udało się wysłać wiadomości. Spróbuj ponownie za chwilę.",
      rateLimit:
        "Przekroczono limit wysyłek z tego adresu. Spróbuj ponownie za godzinę.",
      waitBeforeSubmit: (s: number): string =>
        `Zaczekaj ${s} s przed wysłaniem (ochrona przed botami).`,
      networkError: (msg: string): string =>
        `Błąd sieci: ${msg}. Sprawdź połączenie i spróbuj ponownie.`,
      networkErrorGeneric:
        "Wystąpił nieoczekiwany błąd. Sprawdź połączenie i spróbuj ponownie.",
    },
  },
} as const;

export type CCategoryAdvertisementTranslations = DeepString<typeof pl>;
